import { useRef, useState, type KeyboardEvent } from 'react';
import { clsx } from 'clsx';
import { Icon } from './icons';
import { Popover, type PopoverProps } from './popover';

export interface MenuItem { id: string; label: string; icon?: string; shortcut?: string; disabled?: boolean; destructive?: boolean;
  checked?: boolean; separatorBefore?: boolean; onSelect?: () => void }
export interface MenuProps extends Omit<PopoverProps, 'children' | 'role' | 'triggerMode'> { items: readonly MenuItem[]; longPress?: boolean }
export function Menu({ items, longPress, open: controlled, onOpenChange, className, ...props }: MenuProps) {
  const [localOpen, setLocalOpen] = useState(false); const buffer = useRef(''); const typedAt = useRef(0);
  const change = (open: boolean) => { setLocalOpen(open); onOpenChange?.(open); };
  const keyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    const index = buttons.findIndex(button => button === document.activeElement);
    let target: HTMLButtonElement | undefined;
    if (event.key === 'ArrowDown') target = buttons[(index + 1) % buttons.length];
    else if (event.key === 'ArrowUp') target = buttons[(index - 1 + buttons.length) % buttons.length];
    else if (event.key === 'Home') target = buttons[0]; else if (event.key === 'End') target = buttons.at(-1);
    else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && event.key !== ' ') {
      const now = Date.now(); buffer.current = (now - typedAt.current > 700 ? '' : buffer.current) + event.key.toLocaleLowerCase(); typedAt.current = now;
      target = buttons.find(button => button.textContent?.trim().toLocaleLowerCase().startsWith(buffer.current));
    }
    if (target) { event.preventDefault(); target.focus(); }
  };
  return <Popover {...props} className={clsx('hylo-menu', className)} role="menu" open={controlled ?? localOpen} onOpenChange={change} triggerMode={longPress ? 'long-press' : 'click'}>
    <div role="none" onKeyDown={keyboard}>{items.map(item => <div key={item.id} role="none">
      {item.separatorBefore && <div role="separator" className="hylo-menu__separator" />}
      <button type="button" role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'} aria-checked={item.checked}
        disabled={item.disabled} className={clsx('hylo-menu__item', item.destructive && 'is-destructive')}
        onClick={() => { change(false); item.onSelect?.(); }}>
        {item.icon && <Icon name={item.icon} />}<span>{item.label}</span>{item.shortcut && <kbd>{item.shortcut}</kbd>}
        {item.checked && <Icon name="checkmark" />}</button></div>)}</div>
  </Popover>;
}

/** Same keyboard/context menu; touch holds for 500ms and cancels when the finger scrolls. */
export function LongPressMenu(props: Omit<MenuProps, 'longPress'>) { return <Menu {...props} longPress />; }
